/**
 * Minimal WS client for the Who Am I? browser source.
 *
 * Phase 1 deliberately doesn't include clock-sync (no per-frame deadline
 * scheduling for Who Am I? — the FSM emits frames with `revealed_at_ms` /
 * `next_clue_at_ms` and the round scene's countdown widget converts via
 * `next_clue_at_ms - Date.now()` directly).
 *
 * @packageDocumentation
 */

import type { InMsg, OutMsg } from '@sg/shared';

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface WsClientOpts {
  url: string;
  autoReconnect?: boolean;
  maxBackoffMs?: number;
  heartbeatIntervalMs?: number;
  sessionToken?: string;
  /**
   * Read-only viewer mode for `/watch/<handle>/feed`. When true, the client
   * never sends frames (no hello, no ping). Server closes (4429) on any
   * inbound frame.
   */
  viewer?: boolean;
  onMessage: (m: OutMsg) => void;
  onState?: (s: ConnectionState) => void;
}

export class WsClient {
  private ws: WebSocket | null = null;
  private state: ConnectionState = 'idle';
  private reconnectAttempt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private readonly url: string;
  private readonly autoReconnect: boolean;
  private readonly maxBackoffMs: number;
  private readonly heartbeatIntervalMs: number;
  private readonly sessionToken: string;
  private readonly viewer: boolean;
  private readonly onMessage: (m: OutMsg) => void;
  private readonly onState?: (s: ConnectionState) => void;

  constructor(opts: WsClientOpts) {
    this.url = opts.url;
    this.autoReconnect = opts.autoReconnect ?? true;
    this.maxBackoffMs = opts.maxBackoffMs ?? 30_000;
    this.heartbeatIntervalMs = opts.heartbeatIntervalMs ?? 15_000;
    this.sessionToken = opts.sessionToken ?? '';
    this.viewer = opts.viewer ?? false;
    this.onMessage = opts.onMessage;
    if (opts.onState) this.onState = opts.onState;
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  connect(): void {
    if (this.destroyed) return;
    this.setState('connecting');
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.setState('error');
      this.scheduleReconnect();
      return;
    }
    this.ws.addEventListener('open', this.handleOpen);
    this.ws.addEventListener('message', this.handleMessage);
    this.ws.addEventListener('close', this.handleClose);
    this.ws.addEventListener('error', this.handleError);
  }

  send(msg: InMsg): void {
    if (this.viewer) return; // read-only feed; server closes 4429 on any frame
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  close(): void {
    this.destroyed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* swallow */
      }
      this.ws = null;
    }
  }

  private handleOpen = (): void => {
    this.reconnectAttempt = 0;
    this.setState('open');
    // Viewer mode: no hello, no heartbeat — `/watch/<handle>/feed` is read-only.
    if (this.viewer) return;
    this.send({ type: 'hello', session_token: this.sessionToken, browser_version: '0.1.0' });
    this.startHeartbeat();
  };

  private handleMessage = (ev: MessageEvent): void => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object' || typeof (parsed as { type?: unknown }).type !== 'string') {
      return;
    }
    const msg = parsed as OutMsg;
    if (msg.type === 'pong' || msg.type === 'tick') return;
    this.onMessage(msg);
  };

  private handleClose = (): void => {
    this.stopHeartbeat();
    this.setState('closed');
    this.ws = null;
    this.scheduleReconnect();
  };

  private handleError = (): void => {
    this.setState('error');
  };

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping', t0: performance.now() });
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed || !this.autoReconnect) return;
    const base = Math.min(500 * 2 ** this.reconnectAttempt, this.maxBackoffMs);
    const jitter = Math.random() * 250;
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(), base + jitter);
  }

  private setState(next: ConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.onState?.(next);
  }
}
